import { useMutation, useQueryClient, type UseMutationOptions, type MutationFunction } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { postRequest, deleteRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import type { StreamPost, Reaction, TimelineResponse } from 'src/shared/schemas/stream/feed'

interface PaginatedTimelineResponse {
  pages: TimelineResponse[]
  pageParams: unknown[]
}

interface ToggleLikeVariables {
  activityId: string
  parentType: 'post' | 'comment'
  reactionId?: string
  isLiked: boolean
}

interface MutationContext {
  originalData: StreamPost | Reaction | undefined
  currentData: StreamPost | Reaction | undefined
  previousFeedData: PaginatedTimelineResponse | undefined
  previousCommentsData: Reaction[] | undefined
  originalReactionId?: string
  wasLiked: boolean
}

type ToggleLikeFn = MutationFunction<Reaction | undefined, ToggleLikeVariables>

const toggleLike: ToggleLikeFn = async ({
  activityId,
  parentType,
  reactionId,
  isLiked,
}: ToggleLikeVariables): Promise<Reaction | undefined> => {
  if (isLiked && reactionId && !reactionId.includes('temp')) {
    await deleteRequest(`/feeds/reactions/delete-like?reaction_id=${reactionId}`)
    return undefined
  }

  const response = await postRequest<Reaction>(
    `/feeds/reactions/like?parent_id=${activityId}&parent_type=${parentType}`,
    {},
  )
  if (!response) {
    throw new Error('Failed to like activity')
  }
  return response
}

export const useToggleLike = (
  options?: Omit<UseMutationOptions<Reaction | undefined, Error, ToggleLikeVariables, MutationContext>, 'mutationFn'>,
) => {
  const queryClient = useQueryClient()
  const { data: user } = useMyUserData()
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const pendingMutationRef = useRef<ToggleLikeVariables | undefined>(undefined)
  const contextRef = useRef<MutationContext | undefined>(undefined)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const performOptimisticUpdate = (variables: ToggleLikeVariables) => {
    const { activityId, parentType } = variables
    const optimisticLikeId = `temp-like-${Date.now()}`

    if (!user) {
      return
    }

    const currentData =
      parentType === 'post'
        ? queryClient.getQueryData<StreamPost>(queries.feeds.post(activityId).queryKey)
        : queryClient.getQueryData<Reaction>(queries.feeds.comment(activityId).queryKey)

    const currentFeedData = queryClient.getQueryData<PaginatedTimelineResponse>(queries.feeds.timeline.queryKey)
    const currentCommentsData = queryClient.getQueryData<Reaction[]>(queries.feeds.comment(activityId).queryKey)

    if (!contextRef.current) {
      const wasLiked =
        parentType === 'post'
          ? !!(currentData as StreamPost)?.ownReactions?.like?.length
          : !!(currentData as Reaction)?.hasLiked

      const originalReactionId =
        parentType === 'post'
          ? (currentData as StreamPost)?.ownReactions?.like?.[0]?.id
          : (currentData as Reaction)?.ownLike

      contextRef.current = {
        originalData: currentData,
        currentData,
        previousFeedData: currentFeedData,
        previousCommentsData: currentCommentsData,
        originalReactionId: originalReactionId && !originalReactionId.includes('temp') ? originalReactionId : undefined,
        wasLiked,
      }
    }

    const currentlyLiked =
      parentType === 'post'
        ? !!(currentData as StreamPost)?.ownReactions?.like?.length
        : !!(currentData as Reaction)?.hasLiked

    if (parentType === 'post' && currentData) {
      const updatedPost: StreamPost = {
        ...(currentData as StreamPost),
        reactionCounts: {
          ...(currentData as StreamPost).reactionCounts,
          like: Math.max(0, ((currentData as StreamPost).reactionCounts?.like || 0) + (currentlyLiked ? -1 : 1)),
        },
        ownReactions: {
          ...(currentData as StreamPost).ownReactions,
          like: currentlyLiked ? [] : [{ id: optimisticLikeId, createdAt: new Date().toISOString() }],
        },
      }

      queryClient.setQueryData(queries.feeds.post(activityId).queryKey, updatedPost)

      queryClient.setQueryData<PaginatedTimelineResponse>(queries.feeds.timeline.queryKey, (oldData) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            results: page.results.map((post: StreamPost) => (post.id === activityId ? updatedPost : post)),
          })),
        }
      })

      if (currentData && 'actor' in currentData) {
        const post = currentData as StreamPost
        if (post.actor?.id) {
          queryClient.setQueryData<PaginatedTimelineResponse>(
            queries.feeds.userFeed(Number(post.actor.id)).queryKey,
            (oldData) => {
              if (!oldData) return oldData
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  results: page.results.map((p: StreamPost) => (p.id === activityId ? updatedPost : p)),
                })),
              }
            },
          )
        }
      }

      if (contextRef.current) {
        contextRef.current.currentData = updatedPost
      }
    } else if (currentData) {
      queryClient.setQueryData(queries.feeds.comment(activityId).queryKey, (old: Reaction | undefined) => {
        if (!old) return old
        const updated = {
          ...old,
          childrenCounts: {
            ...old.childrenCounts,
            like: Math.max(0, (old.childrenCounts?.like || 0) + (currentlyLiked ? -1 : 1)),
          },
          hasLiked: !currentlyLiked,
          ownLike: currentlyLiked ? undefined : optimisticLikeId,
        }
        if (contextRef.current) {
          contextRef.current.currentData = updated
        }
        return updated
      })

      if ((currentData as Reaction).activityId) {
        const postId = (currentData as Reaction).activityId
        const parentId = (currentData as Reaction).parent
        const parentType = !parentId ? 'post' : 'comment'

        const parentQueryKey =
          parentType === 'post'
            ? queries.feeds.post(postId)._ctx.comments.queryKey
            : queries.feeds.comment(parentId || '')._ctx.comments.queryKey

        queryClient.setQueryData<Reaction[]>(parentQueryKey, (oldComments) => {
          if (!oldComments) return oldComments
          return oldComments.map((comment) =>
            comment.id === currentData.id
              ? {
                  ...comment,
                  childrenCounts: {
                    ...comment.childrenCounts,
                    like: Math.max(0, (comment.childrenCounts?.like || 0) + (currentlyLiked ? -1 : 1)),
                  },
                  hasLiked: !currentlyLiked,
                  ownLike: currentlyLiked ? undefined : optimisticLikeId,
                }
              : comment,
          )
        })
      }
    }

    const context = contextRef.current
    if (!context) {
      throw new Error('Missing mutation context')
    }
    return context
  }

  return useMutation<Reaction | undefined, Error, ToggleLikeVariables, MutationContext>({
    mutationFn: async (variables) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      pendingMutationRef.current = variables

      return new Promise((resolve, reject) => {
        timeoutRef.current = setTimeout(async () => {
          try {
            if (pendingMutationRef.current === variables && contextRef.current) {
              const currentlyLiked =
                variables.parentType === 'post'
                  ? !!(contextRef.current.currentData as StreamPost)?.ownReactions?.like?.length
                  : !!(contextRef.current.currentData as Reaction)?.hasLiked

              if (currentlyLiked === contextRef.current.wasLiked) {
                resolve(undefined)
                return
              }

              const result = await toggleLike({
                ...variables,
                reactionId: contextRef.current.originalReactionId,
              })
              resolve(result)
            }
          } catch (error) {
            reject(error)
          } finally {
            contextRef.current = undefined
          }
        }, 1000)
      })
    },
    onMutate: async (variables: ToggleLikeVariables) => {
      await queryClient.cancelQueries({ queryKey: queries.feeds._def })
      return performOptimisticUpdate(variables)
    },
    onError: (_error: Error, variables: ToggleLikeVariables, context: MutationContext | undefined) => {
      if (!context) return
      const { activityId, parentType } = variables

      if (parentType === 'post') {
        queryClient.setQueryData(queries.feeds.post(activityId).queryKey, context.originalData)
        if (context.previousFeedData) {
          queryClient.setQueryData(queries.feeds.timeline.queryKey, context.previousFeedData)
        }
      } else {
        queryClient.setQueryData(queries.feeds.comment(activityId).queryKey, context.originalData)
        if (context.previousCommentsData) {
          queryClient.setQueryData(queries.feeds.post(activityId)._ctx.comments.queryKey, context.previousCommentsData)
        }
      }

      contextRef.current = undefined
    },
    onSettled: (_data: Reaction | undefined, _error: Error | null, variables: ToggleLikeVariables) => {
      const { activityId, parentType } = variables
      if (parentType === 'post') {
        queryClient.invalidateQueries({
          queryKey: queries.feeds.post(activityId).queryKey,
        })
        queryClient.invalidateQueries({
          queryKey: queries.feeds.timeline.queryKey,
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: queries.feeds.comment(activityId).queryKey,
        })
        queryClient.invalidateQueries({ queryKey: queries.feeds.post._def })
        queryClient.invalidateQueries({ queryKey: queries.feeds.comment._def })
      }
      contextRef.current = undefined
    },
    ...options,
  })
}
